import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private isBrowser: boolean;

  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  updateMetaTags(route: string, lang: string) {
    const airlineData = this.getAirlineMeta(route, lang);
    if (!airlineData) return;

    this.title.setTitle(airlineData.title);

    this.meta.updateTag({
      name: 'description',
      content: airlineData.description,
    });
    this.meta.updateTag({ name: 'keywords', content: airlineData.keywords });
    this.meta.updateTag({ rel: 'canonical', href: airlineData.canonical });
  }

  getAirlineMeta(route: string, lang: string) {
    const airlines: Record<
      string,
      Record<
        string,
        {
          title: string;
          description: string;
          keywords: string;
          alternateHref: string;
          hreflang: string;
          canonical: string;
        }
      >
    > = {
      'air-cairo-SM': {
        en: {
          title: 'Book the Cheapest Air Cairo Flights | Abousamra Travel',
          description:
            'Book the cheapest Air Cairo flights with Abousamra Travel. Get the best deals, lowest prices, and 24/7 customer support.',
          keywords:
            'Air Cairo flights, book Air Cairo, cheap Air Cairo tickets, Abousamra Travel Air Cairo deals, Air Cairo booking',
          alternateHref: 'https://abousamra.com/en/airlines/air-cairo-SM',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/air-cairo-SM',
        },
        ar: {
          title: 'حجز أرخص رحلات طيران القاهرة | ترافاسكاي',
          description:
            'احجز أرخص تذاكر طيران على متن طيران القاهرة عبر ترافاسكاي. استمتع بأفضل العروض وخدمة عملاء متميزة.',
          keywords:
            'حجز طيران القاهرة, تذاكر طيران القاهرة, رحلات طيران القاهرة, عروض طيران القاهرة, ترافاسكاي طيران القاهرة',
          alternateHref: 'https://abousamra.com/airlines/air-cairo-SM',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/air-cairo-SM',
        },
      },
      'almasria-universal-airlines-UJ': {
        en: {
          title:
            'Book The cheapest almasria universal airlines flights | Abousamra Travel',
          description:
            'Book the cheapest almasria universal airlines flights tickets through Abousamra Travel, and get a unique booking. ✓ Find the lowest price ✓ 24/7 Customer Support.',
          keywords: 'almasria, universal, airlines',
          alternateHref:
            'https://abousamra.com/en/airlines/almasria-universal-airlines-UJ',
          hreflang: 'en-EG',
          canonical:
            'https://abousamra.com/en/airlines/almasria-universal-airlines-UJ',
        },
        ar: {
          title: 'حجز رحلات المصرية العالمية للطيران بأرخص الأسعار | ترافاسكاي',
          description:
            'احجز أرخص تذاكر طيران على متن المصرية العالمية للطيران من خلال ترافاسكاي وتمتع بأفضل العروض. استمتع بخدمة عملاء متميزة وتجربة حجز سهلة وآمنة مع خيارات مرنة.',
          keywords:
            'حجز طيران القاهرة, تذاكر طيران القاهرة, رحلات طيران القاهرة, عروض طيران القاهرة, ترافاسكاي طيران القاهرة',
          alternateHref:
            'https://abousamra.com/airlines/almasria-universal-airlines-UJ',
          hreflang: 'ar-EG',
          canonical:
            'https://abousamra.com/airlines/almasria-universal-airlines-UJ',
        },
      },
      'egyptair-MS': {
        en: {
          title: 'Book the Cheapest EgyptAir Flights | Abousamra Travel',
          description:
            'Get the lowest fares for EgyptAir flights through Abousamra Travel. Enjoy a hassle-free booking experience with the best flight deals.',
          keywords:
            'EgyptAir flights, book EgyptAir, EgyptAir tickets, EgyptAir best deals, Abousamra Travel EgyptAir offers',
          alternateHref: 'https://abousamra.com/en/airlines/egyptair-MS',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/egyptair-MS',
        },
        ar: {
          title: 'حجز أرخص رحلات مصر للطيران | ترافاسكاي',
          description:
            'احجز أرخص تذاكر مصر للطيران عبر ترافاسكاي. استفد من أفضل الأسعار والخدمات المتميزة.',
          keywords:
            'حجز مصر للطيران, تذاكر مصر للطيران, رحلات مصر للطيران, عروض مصر للطيران, ترافاسكاي مصر للطيران',
          alternateHref: 'https://abousamra.com/airlines/egyptair-MS',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/egyptair-MS',
        },
      },
      'flyegypt-FT': {
        en: {
          title: 'Book the Cheapest FlyEgypt Flights | Abousamra Travel',
          description:
            'Find the best deals on FlyEgypt flights with Abousamra Travel. Secure your booking with the lowest fares available.',
          keywords:
            'FlyEgypt flights, book FlyEgypt, FlyEgypt tickets, FlyEgypt deals, Abousamra Travel FlyEgypt booking',
          alternateHref: 'https://abousamra.com/en/airlines/flyegypt-FT',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/flyegypt-FT',
        },
        ar: {
          title: 'حجز أرخص رحلات فلاي ايجيبت | ترافاسكاي',
          description:
            'احجز أرخص تذاكر فلاي ايجيبت عبر ترافاسكاي. استمتع بأفضل الأسعار والخدمات المتميزة.',
          keywords:
            'حجز فلاي ايجيبت, تذاكر فلاي ايجيبت, رحلات فلاي ايجيبت, عروض فلاي ايجيبت, ترافاسكاي فلاي ايجيبت',
          alternateHref: 'https://abousamra.com/airlines/flyegypt-FT',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/flyegypt-FT',
        },
      },
      'nile-air-NP': {
        en: {
          title: 'Book the Cheapest Nile Air Flights | Abousamra Travel',
          description:
            'Get the lowest prices on Nile Air flights through Abousamra Travel. Book your next journey with confidence.',
          keywords:
            'Nile Air flights, book Nile Air, Nile Air tickets, Nile Air deals, Abousamra Travel Nile Air booking',
          alternateHref: 'https://abousamra.com/en/airlines/nile-air-NP',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/nile-air-NP',
        },
        ar: {
          title: 'حجز أرخص رحلات طيران النيل | ترافاسكاي',
          description:
            'احجز أرخص تذاكر طيران النيل عبر ترافاسكاي. استمتع بأفضل الأسعار والخدمات الممتازة.',
          keywords:
            'حجز طيران النيل, تذاكر طيران النيل, رحلات طيران النيل, عروض طيران النيل, ترافاسكاي طيران النيل',
          alternateHref: 'https://abousamra.com/airlines/nile-air-NP',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/nile-air-NP',
        },
      },
      'wizz-air-W6': {
        en: {
          title: 'Book the Cheapest Wizz Air Flights | Abousamra Travel',
          description:
            'Find the best prices on Wizz Air flights at Abousamra Travel. Book now and enjoy a smooth journey at the lowest fares.',
          keywords:
            'Wizz Air flights, book Wizz Air, Wizz Air tickets, Wizz Air deals, Abousamra Travel Wizz Air booking',
          alternateHref: 'https://abousamra.com/en/airlines/wizz-air-W6',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/wizz-air-W6',
        },
        ar: {
          title: 'حجز أرخص رحلات ويز اير | ترافاسكاي',
          description:
            'احجز أرخص تذاكر ويز اير عبر ترافاسكاي. استفد من أفضل العروض والرحلات بأسعار مخفضة.',
          keywords:
            'حجز ويز اير, تذاكر ويز اير, رحلات ويز اير, عروض ويز اير, ترافاسكاي ويز اير',
          alternateHref: 'https://abousamra.com/airlines/wizz-air-W6',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/wizz-air-W6',
        },
      },
      'flynas-XY': {
        en: {
          title: 'Book the Cheapest Flynas Flights | Abousamra Travel',
          description:
            'Book the cheapest Flynas flights tickets through Abousamra Travel, and get a unique booking. ✓ Find the lowest price ✓ 24/7 Customer Support.',
          keywords:
            'Flynas flights, book Flynas, Flynas tickets, Flynas deals, Abousamra Travel Flynas booking',
          alternateHref: 'https://abousamra.com/en/airlines/flynas-XY',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/flynas-XY',
        },
        ar: {
          title: 'حجز أرخص رحلات طيران ناس | ترافاسكاي',
          description:
            'احجز أرخص تذاكر طيران ناس من خلال ترافاسكاي وتمتع بأفضل العروض. استمتع بخدمة عملاء متميزة وتجربة حجز سهلة وآمنة مع خيارات مرنة.',
          keywords:
            'حجز طيران ناس, تذاكر طيران ناس, رحلات طيران ناس, عروض طيران ناس, ترافاسكاي طيران ناس',
          alternateHref: 'https://abousamra.com/airlines/flynas-XY',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/flynas-XY',
        },
      },
      'saudia-airlines-SV': {
        en: {
          title: 'Book the Cheapest Saudia Airlines Flights | Abousamra Travel',
          description:
            'Book the cheapest Saudia Airlines flights tickets through Abousamra Travel, and get a unique booking. ✓ Find the lowest price ✓ 24/7 Customer Support.',
          keywords:
            'Saudia Airlines flights, book Saudia Airlines, Saudia Airlines tickets, Saudia Airlines deals, Abousamra Travel Saudia Airlines booking',
          alternateHref: 'https://abousamra.com/en/airlines/saudia-airlines-SV',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/saudia-airlines-SV',
        },
        ar: {
          title: 'حجز أرخص رحلات الخطوط السعودية | ترافاسكاي',
          description:
            'احجز أرخص تذاكر الخطوط السعودية من خلال ترافاسكاي وتمتع بأفضل العروض. استمتع بخدمة عملاء متميزة وتجربة حجز سهلة وآمنة مع خيارات مرنة.',
          keywords:
            'حجز الخطوط السعودية, تذاكر الخطوط السعودية, رحلات الخطوط السعودية, عروض الخطوط السعودية, ترافاسكاي الخطوط السعودية',
          alternateHref: 'https://abousamra.com/airlines/saudia-airlines-SV',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/saudia-airlines-SV',
        },
      },
      'air-arabia-G9': {
        en: {
          title: 'Book the Cheapest Air Arabia Flights | Abousamra Travel',
          description:
            'Book the cheapest Air Arabia flights tickets through Abousamra Travel, and get a unique booking. ✓ Find the lowest price ✓ 24/7 Customer Support.',
          keywords:
            'Air Arabia flights, book Air Arabia, Air Arabia tickets, Air Arabia deals, Abousamra Travel Air Arabia booking',
          alternateHref: 'https://abousamra.com/en/airlines/air-arabia-G9',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/air-arabia-G9',
        },
        ar: {
          title: 'حجز أرخص رحلات طيران العربية | ترافاسكاي',
          description:
            'احجز أرخص تذاكر طيران العربية من خلال ترافاسكاي وتمتع بأفضل العروض. استمتع بخدمة عملاء متميزة وتجربة حجز سهلة وآمنة مع خيارات مرنة.',
          keywords:
            'حجز طيران العربية, تذاكر طيران العربية, رحلات طيران العربية, عروض طيران العربية, ترافاسكاي طيران العربية',
          alternateHref: 'https://abousamra.com/airlines/air-arabia-G9',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/air-arabia-G9',
        },
      },
      'emirates-EK': {
        en: {
          title: 'Book the Cheapest Emirates Flights | Abousamra Travel',
          description:
            'Book the cheapest Emirates flights tickets through Abousamra Travel, and get a unique booking. ✓ Find the lowest price ✓ 24/7 Customer Support.',
          keywords:
            'Emirates flights, book Emirates, Emirates tickets, Emirates deals, Abousamra Travel Emirates booking',
          alternateHref: 'https://abousamra.com/en/airlines/emirates-EK',
          hreflang: 'en-EG',
          canonical: 'https://abousamra.com/en/airlines/emirates-EK',
        },
        ar: {
          title: 'حجز أرخص رحلات طيران الإمارات | ترافاسكاي',
          description:
            'احجز أرخص تذاكر طيران الإمارات من خلال ترافاسكاي وتمتع بأفضل العروض. استمتع بخدمة عملاء متميزة وتجربة حجز سهلة وآمنة مع خيارات مرنة.',
          keywords:
            'حجز طيران الإمارات, تذاكر طيران الإمارات, رحلات طيران الإمارات, عروض طيران الإمارات, ترافاسكاي طيران الإمارات',
          alternateHref: 'https://abousamra.com/airlines/emirates-EK',
          hreflang: 'ar-EG',
          canonical: 'https://abousamra.com/airlines/emirates-EK',
        },
      },
    };

    const airlineCode = route.split('/').pop() || '';
    return airlines[airlineCode]?.[lang] || null;
  }
}
